/**
 * OffersPageViewModel - Offers Page Orchestration
 * 
 * Manages complete offers page functionality for PageContentOffers component
 * Handles offer loading, grouping, filtering, search, and navigation
 * 
 * Cross-platform implementation with dependency injection
 */

import { useCallback, useState, useEffect, useMemo } from 'react'
import type { IPlatformServices, IOffersPageService, OfferSummary } from '../contracts'
import { safeGetLogger } from '@restaurant/logging'

import type { OfferType } from '@restaurant/core'
import type { 
  PageContentOffersProps, 
  OfferGroupData, 
  TileOfferProps 
} from '@restaurant/core'

// Types for offer data structures
export type OfferFilterType = 'all' | OfferType

// ViewModel state interface
export interface OffersPageViewModelState {
  // Offer data
  offerGroups: OfferGroupData[]
  allOffers: OfferSummary[]
  
  // Search and filtering
  searchQuery: string
  selectedFilter: OfferFilterType
  
  // Loading states
  loading: boolean
  error: string | null
  
  // Computed properties
  isEmpty: boolean
  isSearchMode: boolean
  totalOffersCount: number
  activeOffersCount: number
  upcomingOffersCount: number
}

// ViewModel actions interface
export interface OffersPageViewModelActions {
  // Data management
  loadOffers: () => Promise<void>
  retryLoad: () => Promise<void>
  refresh: () => Promise<void>
  
  // Search and filtering
  setSearchQuery: (query: string) => void
  setSelectedFilter: (filter: OfferFilterType) => void
  
  // Offer interactions
  handleOfferClick: (offer: TileOfferProps, offerIndex: number, groupId?: string) => Promise<void>
  handleGroupHeaderClick: (group: OfferGroupData, groupIndex: number) => void
  
  // Component integration
  getPageContentOffersProps: () => Partial<PageContentOffersProps>
  
  // Utility functions
  clearError: () => void
}

/**
 * Complete OffersPageViewModel interface
 */
export interface OffersPageViewModel extends OffersPageViewModelState, OffersPageViewModelActions {}

/**
 * Dependencies for OffersPageViewModel
 */
export interface OffersPageViewModelDependencies {
  offersPageService?: IOffersPageService
}

/**
 * Platform-agnostic OffersPageViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createOffersPageViewModel = (
  services: IPlatformServices,
  dependencies?: OffersPageViewModelDependencies
) => {
  return (): OffersPageViewModel => {
    // Extract services
    const { navigationService, platformService } = services
    const offersPageService = dependencies?.offersPageService

    // State management
    const [offerGroups, setOfferGroups] = useState<OfferGroupData[]>([])
    const [allOffers, setAllOffers] = useState<OfferSummary[]>([])
    
    // Search and filtering state
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedFilter, setSelectedFilter] = useState<OfferFilterType>('all')
    
    // Loading states
    const [loading, setLoading] = useState(!!offersPageService) // Start loading if service available
    const [error, setError] = useState<string | null>(null)

    // Helper function to get current restaurant ID
    // This would typically come from a restaurant store or context
    const getCurrentRestaurantId = useCallback(() => {
      // For now, return a placeholder - this would be implemented in the web adapter
      return 'current-restaurant-id'
    }, [])

    // Transform OfferSummary to TileOfferProps
    const transformOfferToTileProps = useCallback((offer: OfferSummary): TileOfferProps => {
      return {
        offerName: offer.name,
        buttonText: offer.buttonText,
        disabled: !offer.canClaim,
        onClick: () => {
          // This will be handled by the click handler
        },
        testID: `offer-tile-${offer.id}`,
        accessibilityLabel: `${offer.name} offer. ${offer.description}`,
      }
    }, [])

    // Create offer groups from offers
    const createOfferGroups = useCallback((activeOffers: OfferSummary[], upcomingOffers: OfferSummary[]) => {
      const groups: OfferGroupData[] = []

      // Active Offers group
      if (activeOffers.length > 0) {
        groups.push({
          id: 'active-offers',
          groupName: 'Active Offers',
          offers: activeOffers.map(transformOfferToTileProps),
          disabled: false,
          onOfferClick: (_offer, index) => {
            const originalOffer = activeOffers[index]
            if (originalOffer) {
              handleOfferClick(_offer, index, 'active-offers')
            }
          },
        })
      }

      // Upcoming Offers group
      if (upcomingOffers.length > 0) {
        groups.push({
          id: 'upcoming-offers',
          groupName: 'Upcoming Offers',
          offers: upcomingOffers.map(transformOfferToTileProps),
          disabled: true, // Upcoming offers are not clickable
          onOfferClick: (_offer, _index) => {
            // Upcoming offers are disabled, but we can show a message
            platformService.showToast?.('This offer is not yet available')
          },
        })
      }

      return groups
    }, [transformOfferToTileProps, platformService])

    // Computed properties
    const isEmpty = useMemo(() => {
      return offerGroups.length === 0 || offerGroups.every(group => group.offers.length === 0)
    }, [offerGroups])

    const isSearchMode = useMemo(() => {
      return searchQuery.length > 0
    }, [searchQuery])

    const totalOffersCount = useMemo(() => {
      return allOffers.length
    }, [allOffers])

    const activeOffersCount = useMemo(() => {
      return allOffers.filter(offer => offer.isActive && !offer.isUpcoming).length
    }, [allOffers])

    const upcomingOffersCount = useMemo(() => {
      return allOffers.filter(offer => offer.isUpcoming).length
    }, [allOffers])

    // Load offers functionality
    const loadOffers = useCallback(async () => {
      if (!offersPageService) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const restaurantId = getCurrentRestaurantId()
        
        // Load both active and upcoming offers in parallel
        const [activeOffers, upcomingOffers] = await Promise.allSettled([
          offersPageService.getActiveOffers(restaurantId),
          offersPageService.getUpcomingOffers(restaurantId),
        ])

        let loadedActiveOffers: OfferSummary[] = []
        let loadedUpcomingOffers: OfferSummary[] = []

        // Handle active offers result
        if (activeOffers.status === 'fulfilled') {
          loadedActiveOffers = activeOffers.value
        } else {
          safeGetLogger()?.error('Failed to load active offers', { domain: 'offers', error: String(activeOffers.reason) })
          setError(activeOffers.reason.message || 'Failed to load active offers')
        }

        // Handle upcoming offers result
        if (upcomingOffers.status === 'fulfilled') {
          loadedUpcomingOffers = upcomingOffers.value
        } else {
          safeGetLogger()?.error('Failed to load upcoming offers', { domain: 'offers', error: String(upcomingOffers.reason) })
          // Only set error if active offers also failed
          if (activeOffers.status === 'rejected') {
            setError(upcomingOffers.reason.message || 'Failed to load offers')
          }
        }

        // Update state with loaded offers
        const combinedOffers = [...loadedActiveOffers, ...loadedUpcomingOffers]
        setAllOffers(combinedOffers)
        setOfferGroups(createOfferGroups(loadedActiveOffers, loadedUpcomingOffers))

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load offers'
        setError(errorMessage)
        setAllOffers([])
        setOfferGroups([])
      } finally {
        setLoading(false)
      }
    }, [offersPageService, getCurrentRestaurantId, createOfferGroups])

    const retryLoad = useCallback(async () => {
      await loadOffers()
    }, [loadOffers])

    // Refresh functionality
    const refresh = useCallback(async () => {
      if (!offersPageService) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const restaurantId = getCurrentRestaurantId()
        const refreshedOffers = await offersPageService.refreshOffers(restaurantId)
        
        // Separate active and upcoming offers
        const activeOffers = refreshedOffers.filter(offer => offer.isActive && !offer.isUpcoming)
        const upcomingOffers = refreshedOffers.filter(offer => offer.isUpcoming)
        
        setAllOffers(refreshedOffers)
        setOfferGroups(createOfferGroups(activeOffers, upcomingOffers))
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to refresh offers'
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }, [offersPageService, getCurrentRestaurantId, createOfferGroups])

    // Search functionality
    const handleSetSearchQuery = useCallback((query: string) => {
      setSearchQuery(query)
      setError(null)

      if (!offersPageService) {
        return
      }

      try {
        if (!query.trim()) {
          // Reset to original groups when search is cleared
          const activeOffers = allOffers.filter(offer => offer.isActive && !offer.isUpcoming)
          const upcomingOffers = allOffers.filter(offer => offer.isUpcoming)
          setOfferGroups(createOfferGroups(activeOffers, upcomingOffers))
          return
        }

        // Perform search
        const searchResults = offersPageService.searchOffers(allOffers, query)
        
        // Group search results
        const activeSearchResults = searchResults.filter(offer => offer.isActive && !offer.isUpcoming)
        const upcomingSearchResults = searchResults.filter(offer => offer.isUpcoming)
        
        setOfferGroups(createOfferGroups(activeSearchResults, upcomingSearchResults))
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Search failed'
        setError(errorMessage)
      }
    }, [offersPageService, allOffers, createOfferGroups])

    // Filter functionality
    const handleSetSelectedFilter = useCallback((filter: OfferFilterType) => {
      setSelectedFilter(filter)
      setError(null)

      if (!offersPageService) {
        return
      }

      try {
        let filteredOffers = allOffers

        if (filter !== 'all') {
          filteredOffers = offersPageService.filterOffersByType(allOffers, filter)
        }

        // Group filtered results
        const activeFiltered = filteredOffers.filter(offer => offer.isActive && !offer.isUpcoming)
        const upcomingFiltered = filteredOffers.filter(offer => offer.isUpcoming)
        
        setOfferGroups(createOfferGroups(activeFiltered, upcomingFiltered))
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Filter failed'
        setError(errorMessage)
      }
    }, [offersPageService, allOffers, createOfferGroups])

    // Offer interaction handlers
    const handleOfferClick = useCallback(async (
      _offer: TileOfferProps,
      offerIndex: number,
      groupId?: string
    ) => {
      try {
        // Find the original offer data
        let targetOfferId: string | undefined

        if (groupId === 'active-offers') {
          const activeOffers = allOffers.filter(o => o.isActive && !o.isUpcoming)
          targetOfferId = activeOffers[offerIndex]?.id
        } else if (groupId === 'upcoming-offers') {
          const upcomingOffers = allOffers.filter(o => o.isUpcoming)
          targetOfferId = upcomingOffers[offerIndex]?.id
        }

        if (targetOfferId) {
          await navigationService.navigate(`/offers/${targetOfferId}`)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Navigation failed'
        setError(errorMessage)
      }
    }, [allOffers, navigationService])

    const handleGroupHeaderClick = useCallback((_group: OfferGroupData, _groupIndex: number) => {
      // Group header clicks could be used for analytics or expanding/collapsing.
      // For now, we track the interaction silently (no-op).
    }, [])

    // Component integration
    const getPageContentOffersProps = useCallback((): Partial<PageContentOffersProps> => {
      const props: Partial<PageContentOffersProps> = {
        offerGroups,
        loading,
        showDefaultGroups: true,
        onOfferClick: (offer, _groupIndex, offerIndex, groupId) => {
          handleOfferClick(offer, offerIndex, groupId)
        },
        testID: 'offers-page',
        accessibilityLabel: 'Offers page with active and upcoming offers',
      }

      // Add empty state content when no offers
      if (isEmpty && !loading) {
        // Provide a simple empty state indicator
        props.emptyStateContent = 'No offers available. Check back later for new offers and promotions!'
      }

      return props
    }, [
      offerGroups,
      loading,
      isEmpty,
      handleOfferClick,
    ])

    // Utility functions
    const clearError = useCallback(() => {
      setError(null)
    }, [])

    // Load offers on mount
    useEffect(() => {
      loadOffers()
    }, [loadOffers])

    return {
      // State
      offerGroups,
      allOffers,
      searchQuery,
      selectedFilter,
      loading,
      error,
      isEmpty,
      isSearchMode,
      totalOffersCount,
      activeOffersCount,
      upcomingOffersCount,

      // Actions
      loadOffers,
      retryLoad,
      refresh,
      setSearchQuery: handleSetSearchQuery,
      setSelectedFilter: handleSetSelectedFilter,
      handleOfferClick,
      handleGroupHeaderClick,
      getPageContentOffersProps,
      clearError,
    }
  }
}